import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { readStoredAdminFlag } from "./authAdmin";
import { getApiBase } from "./apiBase";
import { buildPluginsHeader, isPluginEnabled, PLUGIN_IDS } from "./plugins/registry";
import {
  ADMINISTRATOR_TYPE_ID,
  canViewerPermission,
  getViewerEmailFromStorage,
  hydrateUserMatrixFromServer,
  isImpersonating,
  loadUserMatrixSettings,
  startImpersonation,
} from "./plugins/userMatrixStorage";
import { fetchAppConfig } from "./plugins/appConfigApi";
import { postAdminResetPassword } from "./plugins/adminUsersApi";
import "./App.css";
import "./plugins/workflowSchedule.css";

const AUTH_TOKEN_KEY = "projectBoardAuthToken";

export default function AdminUsersPage() {
  const [, bump] = useReducer((x) => x + 1, 0);
  const [unassigned, setUnassigned] = useState([]);
  const [unassignedError, setUnassignedError] = useState("");
  const [resetStatus, setResetStatus] = useState("");
  const [resettingEmail, setResettingEmail] = useState("");
  useEffect(() => {
    const on = () => bump();
    window.addEventListener("pb-user-matrix-settings-changed", on);
    return () => window.removeEventListener("pb-user-matrix-settings-changed", on);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchAppConfig()
      .then((cfg) => {
        if (cancelled || !cfg?.userMatrix) return;
        hydrateUserMatrixFromServer(cfg.userMatrix);
      })
      .catch(() => {
        /* keep cached matrix if refresh fails */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const matrix = loadUserMatrixSettings();
  const typeById = useMemo(() => {
    const m = new Map();
    for (const t of matrix.userTypes || []) {
      m.set(t.id, t.name);
    }
    return m;
  }, [matrix.userTypes]);

  const rows = useMemo(
    () => [...(matrix.users || [])].sort((a, b) => a.email.localeCompare(b.email)),
    [matrix.users]
  );
  const viewerEmail = getViewerEmailFromStorage();
  const canManageMatrix = canViewerPermission(viewerEmail, "canManageUserMatrix", true);
  const impersonationEnabled = isPluginEnabled(PLUGIN_IDS.USER_IMPERSONATION);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem(AUTH_TOKEN_KEY) || "";
    const headers = {
      ...buildPluginsHeader(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    axios
      .get(`${getApiBase()}/admin/auth-users-unassigned`, { headers })
      .then((res) => {
        if (cancelled) return;
        setUnassigned(Array.isArray(res.data?.users) ? res.data.users : []);
        setUnassignedError("");
      })
      .catch((err) => {
        if (cancelled) return;
        setUnassigned([]);
        setUnassignedError(err?.response?.data?.error || err?.message || "Could not load unassigned users.");
      });
    return () => {
      cancelled = true;
    };
  }, [rows]);

  const sendResetEmail = useCallback(async (targetEmail) => {
    const em = String(targetEmail || "").trim().toLowerCase();
    if (!em) return;
    setResetStatus("");
    setResettingEmail(em);
    try {
      const result = await postAdminResetPassword({
        email: em,
        appOrigin: window.location.origin,
      });
      if (result?.sent) {
        setResetStatus(`Password reset email sent to ${result.email || em}.`);
      } else {
        setResetStatus(
          `Reset link created for ${result.email || em}, but email was not sent (SMTP may be off).`
        );
      }
    } catch (err) {
      setResetStatus(err?.response?.data?.error || err?.message || "Could not send reset email.");
    } finally {
      setResettingEmail("");
    }
  }, []);

  if (!readStoredAdminFlag() || isImpersonating() || !canManageMatrix) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="plugins-page plugins-page--settings">
      <div className="settings-page-inner">
      <div className="plugins-page-header">
        <h1 className="plugins-page-title">Users</h1>
        <p className="plugins-page-lead">
          Directory of everyone in the User Matrix (shared across all browsers). Roles and invitations are managed under{" "}
          <Link to="/plugins">Plugins → User Matrix</Link>.
        </p>
      </div>

      <div className="plugins-card" style={{ padding: "1rem 1.25rem", marginBottom: "1.5rem" }}>
        <table className="admin-users-table" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "0.35rem 0.5rem" }}>Email</th>
              <th style={{ textAlign: "left", padding: "0.35rem 0.5rem" }}>Role (type)</th>
              <th style={{ textAlign: "left", padding: "0.35rem 0.5rem" }}>Status</th>
              <th style={{ textAlign: "left", padding: "0.35rem 0.5rem" }}>Password</th>
              <th style={{ textAlign: "left", padding: "0.35rem 0.5rem" }}>Impersonate</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id}>
                <td style={{ padding: "0.35rem 0.5rem" }}>
                  <code>{u.email}</code>
                </td>
                <td style={{ padding: "0.35rem 0.5rem" }}>
                  {typeById.get(u.userTypeId) || u.userTypeId}
                  {u.userTypeId === ADMINISTRATOR_TYPE_ID ? (
                    <span className="email-triggers-muted" style={{ marginLeft: "0.35rem" }}>
                      (Administrator)
                    </span>
                  ) : null}
                </td>
                <td style={{ padding: "0.35rem 0.5rem" }}>{u.status === "pending" ? "Pending invite" : "Active"}</td>
                <td style={{ padding: "0.35rem 0.5rem" }}>
                  {u.status === "pending" ? (
                    <span className="email-triggers-muted">—</span>
                  ) : (
                    <button
                      type="button"
                      className="link-button"
                      disabled={resettingEmail === u.email}
                      onClick={() => sendResetEmail(u.email)}
                    >
                      {resettingEmail === u.email ? "Sending…" : "Reset password"}
                    </button>
                  )}
                </td>
                <td style={{ padding: "0.35rem 0.5rem" }}>
                  {impersonationEnabled ? (
                    <button
                      type="button"
                      className="user-impersonate-button"
                      title={`Impersonate ${u.email}`}
                      aria-label={`Impersonate ${u.email}`}
                      onClick={() =>
                        startImpersonation({
                          userTypeId: u.userTypeId,
                          email: u.email,
                          roleName: typeById.get(u.userTypeId) || u.userTypeId,
                        })
                      }
                    >
                      🥷
                    </button>
                  ) : (
                    <span className="email-triggers-muted">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {resetStatus ? (
          <p className="email-triggers-muted" style={{ marginTop: "0.75rem" }}>
            {resetStatus}
          </p>
        ) : null}
        {rows.length === 0 ? (
          <p className="email-triggers-muted" style={{ marginTop: "0.75rem" }}>
            No users yet. Enable User Matrix under Plugins and add people there.
          </p>
        ) : null}
      </div>

      <div className="plugins-card" style={{ padding: "1rem 1.25rem", marginBottom: "1.5rem" }}>
        <h3 className="plugins-card-name" style={{ marginTop: 0 }}>Unassigned Auth Users</h3>
        <p className="plugins-card-desc">
          Accounts that can authenticate but are not mapped to a User Matrix profile.
        </p>
        {unassignedError ? <p className="email-triggers-muted">{unassignedError}</p> : null}
        {!unassignedError && unassigned.length === 0 ? (
          <p className="email-triggers-muted">No unassigned users.</p>
        ) : null}
        {!unassignedError && unassigned.length > 0 ? (
          <ul>
            {unassigned.map((u) => (
              <li key={u.id}>
                <code>{u.email}</code>{" "}
                <button
                  type="button"
                  className="link-button"
                  disabled={resettingEmail === u.email}
                  onClick={() => sendResetEmail(u.email)}
                >
                  {resettingEmail === u.email ? "Sending…" : "Reset password"}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <p className="plugins-page-footer">
        <Link to="/plugins" className="project-detail-back">
          ← Plugins &amp; User Matrix settings
        </Link>
      </p>
      <p className="plugins-page-footer">
        <Link to="/" className="project-detail-back">
          ← Back to board
        </Link>
      </p>
      </div>
    </div>
  );
}
